
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    getAssignmentMarksByGradeApi,
    getLessionDetailsByGradeApi,
    getQuizDetailsByGradeCodeApi,
    getStudentSubjectsApi
} from "@/api/reportingApi";

import { GradeDetailInterface } from "@/ts/interfaces/Reporting/ReportingInterface";
import {
    mapGradeAssignmentMarksByTermWithApiData,
    mapGradeLessonDetailsByTermWithApiData,
    mapQuizDetailsWithActivitiesApiData,
    mapStudentSubjectsApiData
} from "@/utils/reportingDataMapper";
import {
    calculateAssignmentSummaryPercentage,
    calculateLessonPercentage,
    calculateQuizesSummaryPercentage,
    calculateTotalLessons,
    mappingGradeDetailByTerm
} from "@/utils/reportingHelper";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { GradeDetailParams } from "../slices/gradeDetailSlice";

export interface GradeDetailResult {
    eukaId: string;
    gradeCode: string;
    details: GradeDetailInterface;
    quizzesSummary: any;
    lessonSummary: any;
    assignmentSummary: any;
    totalLessonCount: number;
    isQuizAvailable: boolean;
}

export const getGradeDetailThunk = createAsyncThunk<
    GradeDetailResult,
    GradeDetailParams,
    { rejectValue: string }
>("reporting/gradeDetail", async (params, { rejectWithValue }) => {
    try {
        const { eukaId, uniqueCourseIds, studentId, gradeCode } = params;

        const [lessonResult, quizResult, assignmentResult, subjectResult] =
            await Promise.allSettled([
                getLessionDetailsByGradeApi(eukaId, gradeCode, uniqueCourseIds),
                getQuizDetailsByGradeCodeApi(eukaId, gradeCode),
                getAssignmentMarksByGradeApi(eukaId, uniqueCourseIds),
                getStudentSubjectsApi(studentId, gradeCode)
            ]);

        let hasLessonError = false;
        let hasQuizError = false;
        let hasAssignmentError = false;
        let hasSubjectError = false;

        const lessonRes =
            lessonResult.status === "fulfilled"
                ? lessonResult.value
                : (() => {
                      hasLessonError = true;
                      return undefined;
                  })();
        const quizRes =
            quizResult.status === "fulfilled"
                ? quizResult.value
                : (() => {
                      hasQuizError = true;
                      return undefined;
                  })();
        const assignmentRes =
            assignmentResult.status === "fulfilled"
                ? assignmentResult.value
                : (() => {
                      hasAssignmentError = true;
                      return undefined;
                  })();
        const subjectRes =
            subjectResult.status === "fulfilled"
                ? subjectResult.value
                : (() => {
                      hasSubjectError = true;
                      return undefined;
                  })();

        const lessonResponseData =
            lessonRes?.data?.data && Array.isArray(lessonRes.data.data)
                ? mapGradeLessonDetailsByTermWithApiData(lessonRes.data.data)
                : [];
        const lessonDataByGrade = lessonResponseData || [];

        const quizResponseData =
            quizRes?.data?.data && Array.isArray(quizRes.data.data)
                ? mapQuizDetailsWithActivitiesApiData(quizRes.data.data)
                : [];
        const isQuizAvailable: boolean = quizResponseData.length > 0;

        const assignmentResponseData =
            assignmentRes &&
            mapGradeAssignmentMarksByTermWithApiData(assignmentRes.data.data);

        const subjectResponseData =
            subjectRes && subjectRes.data && subjectRes.data.data
                ? mapStudentSubjectsApiData(
                      subjectRes.data.data,
                      gradeCode.toString()
                  )
                : {
                      terms: [],
                      electives: [],
                      additionalSubjects: [],
                      hasUniversityPathway: false
                  };

        const quizzesSummary = calculateQuizesSummaryPercentage(
            quizResponseData || []
        );
        const totalLessonCount = calculateTotalLessons(
            subjectResponseData || []
        );

        const details = mappingGradeDetailByTerm(
            subjectResponseData,
            quizResponseData || [],
            lessonDataByGrade,
            assignmentResponseData || [],
            hasQuizError,
            hasLessonError,
            hasAssignmentError,
            hasSubjectError
        );

        const lessonSummary = calculateLessonPercentage(
            lessonDataByGrade,
            totalLessonCount
        );

        const assignmentSummary = calculateAssignmentSummaryPercentage(
            assignmentResponseData || []
        );

        return {
            eukaId,
            gradeCode: gradeCode.toString(),
            details,
            quizzesSummary,
            lessonSummary,
            assignmentSummary,
            totalLessonCount,
            isQuizAvailable
        };
    } catch (err: any) {
        return rejectWithValue(err?.message ?? "Failed to load grade detail.");
    }
});
